// GAVION open-pixel: open tracking for Graph-sent (Outlook) sequence and one-to-one mail.
// SES mail keeps its own open events (apply_ses_event); this file is never used on the SES path.
//
// The pixel is inserted by the server AFTER every sanitiser and allowlist check, so a user can never
// inject it (the image allowlist in emailImages.ts does not accept the functions host). The token is an
// HMAC of the email_messages id under UNSUBSCRIBE_SECRET ("open:" domain, see tokens.ts).
// Privacy (Law 25): no IP is ever stored; the user agent is reduced to a coarse class here.
import { escapeHtml, isHttpsUrl } from "./render.ts";
import { signOpenToken } from "./tokens.ts";
import type { Lang, TemplateKind } from "./types.ts";

/** The classic 43-byte transparent 1x1 GIF89a. */
export const OPEN_PIXEL_GIF: Uint8Array = Uint8Array.from([
  0x47, 0x49, 0x46, 0x38, 0x39, 0x61, 0x01, 0x00, 0x01, 0x00, 0x80, 0x00, 0x00,
  0x00, 0x00, 0x00, 0xff, 0xff, 0xff, 0x21, 0xf9, 0x04, 0x01, 0x00, 0x00, 0x00,
  0x00, 0x2c, 0x00, 0x00, 0x00, 0x00, 0x01, 0x00, 0x01, 0x00, 0x00, 0x02, 0x02,
  0x44, 0x01, 0x00, 0x3b,
]);

/** Law 25 / CASL transparency line, added to the mail only when the pixel is inserted. */
export const OPEN_PIXEL_NOTICE: Readonly<Record<Lang, string>> = Object.freeze({
  fr: "Ce courriel peut contenir un pixel mesurant son ouverture.",
  en: "This email may contain a pixel that measures when it is opened.",
});

/** Only sequence and one-to-one mail carry a pixel (never transactional / demo confirmations). */
export const openPixelAllowedFor = (
  kind: TemplateKind | null,
  enabled: boolean,
): boolean => enabled && (kind === "sequence" || kind === "one_to_one");

export async function buildOpenPixelUrl(
  functionsBaseUrl: string,
  secret: string,
  messageId: number,
): Promise<string> {
  const s = await signOpenToken(secret, messageId);
  return `${functionsBaseUrl}/gavion-track?o=${messageId}&s=${s}`;
}

export function openPixelImg(url: string): string {
  if (!isHttpsUrl(url)) throw new Error("open_pixel_url_unsafe");
  return (
    `<img src="${escapeHtml(url)}" width="1" height="1" alt="" ` +
    `style="display:block;border:0;width:1px;height:1px;">`
  );
}

/** `snippet` just before the last </body>, else at the end. */
export function insertBeforeBodyEnd(html: string, snippet: string): string {
  const re = /<\/body\s*>/gi;
  let at = -1;
  for (const m of html.matchAll(re)) at = m.index ?? at;
  return at === -1
    ? html + snippet
    : html.slice(0, at) + snippet + html.slice(at);
}

/** Just before the last </body>, else at the end. */
export const insertOpenPixel = (html: string, url: string): string =>
  insertBeforeBodyEnd(html, openPixelImg(url));

/** Coarse user-agent class: the only thing stored about who loaded the pixel. */
export type OpenUaClass =
  | "none"
  | "apple_mpp"
  | "google_proxy"
  | "proxy"
  | "scanner"
  | "outlook"
  | "apple_mail"
  | "browser"
  | "other";

const SCANNER =
  /barracuda|mimecast|proofpoint|defender|safelinks|symantec|messagelabs|forcepoint|trend ?micro|sophos|ironport|fortinet|zscaler|scanner|\bbot\b|bot\/|crawler|spider|headless|python|curl|wget|go-http-client|java\/|okhttp|libwww|httpclient/i;

export function classifyOpenUserAgent(ua: string | null): OpenUaClass {
  const s = (ua ?? "").trim();
  if (!s) return "none";
  // Apple Mail Privacy Protection fetches every image through a proxy whose UA is just "Mozilla/5.0".
  if (s === "Mozilla/5.0") return "apple_mpp";
  if (/GoogleImageProxy|ggpht\.com/i.test(s)) return "google_proxy";
  if (/YahooMailProxy/i.test(s)) return "proxy";
  if (SCANNER.test(s)) return "scanner";
  if (/outlook|microsoft office|ms-office/i.test(s)) return "outlook";
  if (
    /(iPhone|iPad|Macintosh)[^)]*\).*AppleWebKit/.test(s) &&
    !/Chrome|Safari|Firefox|Edg/.test(s)
  )
    return "apple_mail";
  if (/Chrome|Safari|Firefox|Edg|Gecko/.test(s)) return "browser";
  return "other";
}

const AUTO_CLASSES: ReadonlySet<OpenUaClass> = new Set([
  "none",
  "apple_mpp",
  "scanner",
]);
export const AUTO_OPEN_WINDOW_MS = 2_000;

/**
 * TS twin of record_email_open's flag (SQL decides; 20261013160000): Apple MPP, scanners and a
 * missing UA, or an open within 2 s of the send (or before the send is recorded). The Google
 * image proxy counts as a real open (it fetches when the message is displayed).
 */
export function isLikelyAutoOpen(
  cls: OpenUaClass,
  sentAt: Date | null,
  at: Date,
): boolean {
  if (AUTO_CLASSES.has(cls)) return true;
  if (!sentAt) return true;
  return at.getTime() - sentAt.getTime() < AUTO_OPEN_WINDOW_MS;
}
